import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "@App/state";
import { Garage } from "@App/types";
import { getMaxBy, getMinBy, hasItems } from "@App/utils/array";
import { GaragePaginator } from "@App/apis/graphql/perimeterSearch/responses/garagePaginator";
import {
  garageAuthorizedForBrand,
  garageWithEarliestAvailability,
} from "@App/utils/garages/badgesUtil";
import { Organizations } from "@App/types/enums";

type UseGarageBadgesParams = {
  garagePaginator: GaragePaginator | null;
};
export const useGarageBadges = ({ garagePaginator }: UseGarageBadgesParams) => {
  // store slices used for resets / badges / recommendations
  const brand = useSelector((s: State) => s.orders.brand);
  const organization = useSelector((s: State) => s.orders.organization);
  const search = useSelector((s: State) => s.filters.search);

  // badges
  const [nearestGarage, setNearestGarage] = useState<Garage | null>(null);
  const [bestReviewedGarage, setBestReviewedGarage] = useState<Garage | null>(null);
  const [earliestAvailableGarage, setEarliestAvailableGarage] = useState<Garage | null>(null);
  const [authorizedGarage, setAuthorizedGarage] = useState<Garage | null>(null);

  useEffect(() => {
    if (!garagePaginator?.data || organization.id !== Organizations.REPAREO) return;

    setNearestGarage(null);
    setBestReviewedGarage(null);
    setEarliestAvailableGarage(null);

    if (search || !hasItems(garagePaginator?.data)) return;

    let garagesForBadges = garagePaginator.data.filter((garage) => garage.pivot.bookable);
    let shouldShowBadges = garagesForBadges.length > 1;

    if (!shouldShowBadges) return;

    setNearestGarage(getMinBy(garagesForBadges, "distance") ?? null);
    setBestReviewedGarage(getMaxBy(garagesForBadges, "rating") ?? null);
    setAuthorizedGarage(garageAuthorizedForBrand(garagesForBadges, brand?.id ?? null));
    setEarliestAvailableGarage(garageWithEarliestAvailability(garagesForBadges));
  }, [garagePaginator]);

  return {
    nearestGarage,
    bestReviewedGarage,
    earliestAvailableGarage,
    authorizedGarage,
  };
};
